import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize } from 'rxjs';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Httpservice } from '../../../services/httpservice';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { createManageMenuFormValue } from './manage-menu.data';
import { ManageMenuItem, ManageMenuPagination } from './manage-menu.interfaces';

type ManageMenuModalMode = 'create' | 'edit';

@Component({
  selector: 'app-manage-menu-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './manage-menu.html',
  styleUrl: './manage-menu.scss',
})
export class ManageMenuPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filterForm: UntypedFormGroup;
  protected readonly menuForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected menuModalOpen = false;
  protected formSubmitted = false;
  protected modalMode: ManageMenuModalMode = 'create';
  protected selectedMenu: ManageMenuItem | null = null;
  protected menus: ManageMenuItem[] = [];
  protected pagination: ManageMenuPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;
    this.menuForm = this.fb.group({
      menu_name: ['', [Validators.required, Validators.maxLength(100)]],
      menu_code: ['', [Validators.required, Validators.maxLength(100)]],
      parent_id: [null],
      route_path: ['', [Validators.maxLength(255)]],
      icon: ['', [Validators.maxLength(100)]],
      display_order: [null],
      phase: [1, [Validators.required]],
      is_active: [true],
    });
  }

  ngOnInit(): void {
    this.loadMenus(1);
    this.filterForm.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadMenus(1));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.mobileSidebarOpen = !this.mobileSidebarOpen;
      return;
    }
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.patchValue({ search: '', page_size: 15 });
    this.loadMenus(1);
  }

  protected openAddMenuModal(): void {
    this.modalMode = 'create';
    this.selectedMenu = null;
    this.formSubmitted = false;
    this.menuForm.reset(createManageMenuFormValue());
    this.menuModalOpen = true;
  }

  protected openEditMenuModal(menu: ManageMenuItem): void {
    this.modalMode = 'edit';
    this.selectedMenu = menu;
    this.formSubmitted = false;
    this.menuForm.reset({
      menu_name: menu.menu_name,
      menu_code: menu.menu_code,
      parent_id: menu.parent_id,
      route_path: menu.route_path ?? '',
      icon: menu.icon ?? '',
      display_order: menu.display_order,
      phase: menu.phase || 1,
      is_active: menu.is_active,
    });
    this.menuModalOpen = true;
  }

  protected closeMenuModal(): void {
    this.menuModalOpen = false;
    this.selectedMenu = null;
    this.formSubmitted = false;
  }

  protected saveMenu(): void {
    this.formSubmitted = true;
    if (this.menuForm.invalid) {
      this.menuForm.markAllAsTouched();
      return;
    }

    const payload = this.buildMenuPayload();
    const endpoint = this.modalMode === 'create' ? AuthEndPoints.ADD_MENU : AuthEndPoints.EDIT_MENU;
    const method = this.modalMode === 'create' ? ApiMethod.POST : ApiMethod.PATCH;

    this.httpSvc
      .requestCall(endpoint, method, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeMenuModal();
          this.loadMenus(this.pagination.page_no);
        },
      });
  }

  protected deleteMenu(menu: ManageMenuItem): void {
    if (typeof window !== 'undefined' && !window.confirm(`Delete ${menu.menu_name}?`)) {
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_MENU, ApiMethod.DELETE, { id: menu.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadMenus(this.pagination.page_no),
      });
  }

  protected getParentName(menu: ManageMenuItem): string {
    if (!menu.parent_id) {
      return '-';
    }
    return this.menus.find((item) => item.id === menu.parent_id)?.menu_name ?? '-';
  }

  protected getAvailableParents(): ManageMenuItem[] {
    const selectedId = this.selectedMenu?.id ?? null;
    return this.menus.filter((menu) => menu.id !== selectedId);
  }

  protected pageNumbers(): number[] {
    const totalPages = this.pagination.total_pages || 1;
    const current = this.pagination.page_no || 1;
    const start = Math.max(1, current - 2);
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages || pageNo === this.pagination.page_no) {
      return;
    }
    this.loadMenus(pageNo);
  }

  protected trackByMenuId(_: number, item: ManageMenuItem): number {
    return item.id;
  }

  protected fieldError(fieldName: string, message: string): string {
    const control = this.menuForm.get(fieldName);
    return this.formSubmitted && control?.invalid ? message : '\u00A0';
  }

  private loadMenus(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_MENUS, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.finishLoading()),
      )
      .subscribe({
        next: (response) => {
          const value = this.extractValue(response);
          this.menus = value && typeof value === 'object' && Array.isArray((value as { menus?: unknown }).menus)
            ? (value as { menus: unknown[] }).menus.map((item) => this.mapMenuItem(item))
            : [];
          this.pagination = this.extractPagination(value) ?? this.pagination;
          this.finishLoading();
        },
        error: () => {
          this.menus = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 15),
            total_records: 0,
            total_pages: 0,
          };
          this.finishLoading();
        },
      });
  }

  private finishLoading(): void {
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private buildMenuPayload(): Record<string, unknown> {
    const formValue = this.menuForm.getRawValue();
    const payload: Record<string, unknown> = {
      menu_name: String(formValue.menu_name ?? '').trim(),
      menu_code: String(formValue.menu_code ?? '').trim(),
      parent_id: this.toNullableNumber(formValue.parent_id),
      route_path: String(formValue.route_path ?? '').trim() || null,
      icon: String(formValue.icon ?? '').trim() || null,
      display_order: this.toNullableNumber(formValue.display_order) ?? 0,
      phase: Number(formValue.phase || 1),
      is_active: Boolean(formValue.is_active),
    };

    if (this.modalMode === 'edit' && this.selectedMenu) {
      payload['id'] = this.selectedMenu.id;
    }

    return payload;
  }

  private buildQueryString(params: Record<string, unknown>): string {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        query.set(key, String(value));
      }
    });
    const text = query.toString();
    return text ? `?${text}` : '';
  }

  private extractValue(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const value = (response as { value?: unknown }).value;
    return value && typeof value === 'object' ? value : null;
  }

  private extractPagination(value: unknown): ManageMenuPagination | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const pagination = (value as { pagination?: unknown }).pagination;
    if (!pagination || typeof pagination !== 'object') {
      return null;
    }
    const record = pagination as Record<string, unknown>;
    return {
      page_no: Number(record['page_no'] ?? 1),
      page_size: Number(record['page_size'] ?? 15),
      total_records: Number(record['total_records'] ?? 0),
      total_pages: Number(record['total_pages'] ?? 0),
    };
  }

  private mapMenuItem(item: unknown): ManageMenuItem {
    const record = (item ?? {}) as Record<string, unknown>;
    const displayOrder = Number(record['display_order'] ?? record['order_by'] ?? 0);
    return {
      id: Number(record['id']),
      serial_no: Number(record['serial_no'] ?? displayOrder),
      menu_name: typeof record['menu_name'] === 'string' ? record['menu_name'] : '',
      menu_code: typeof record['menu_code'] === 'string' ? record['menu_code'] : '',
      parent_id: this.toNullableNumber(record['parent_id'] ?? record['parent_menu_id']),
      parent_menu_id: this.toNullableNumber(record['parent_menu_id'] ?? record['parent_id']),
      route_path: typeof record['route_path'] === 'string' ? record['route_path'] : null,
      menu_url: typeof record['menu_url'] === 'string' ? record['menu_url'] : null,
      icon: typeof record['icon'] === 'string' ? record['icon'] : null,
      icon_class: typeof record['icon_class'] === 'string' ? record['icon_class'] : null,
      display_order: displayOrder,
      order_by: displayOrder,
      phase: Number(record['phase'] ?? 1),
      is_active: record['is_active'] !== false,
    };
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }
}
